import React, { useState } from 'react';
import { Modal, Button, Alert, Form, Badge } from 'react-bootstrap';
import { Archive, Package, Info } from 'lucide-react';

/**
 * ✅ Modal de confirmation d'archivage (soft delete)
 * Volontairement plus léger que DeleteConfirmModal : l'archivage est réversible.
 */
const ArchiveConfirmModal = ({
  show,
  onHide,
  onConfirm,
  product,
  loading = false
}) => {

  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  if (!product) return null;

  const REASONS = [
    'Fin de série',
    'Produit saisonnier',
    'Fournisseur indisponible',
    'Erreur de saisie',
    'Autre'
  ];

  const finalReason = reason === 'Autre' ? customReason.trim() : reason;
  const isValid = reason !== '' && (reason !== 'Autre' || customReason.trim().length > 0);

  const handleConfirm = () => {
    if (isValid && !loading) {
      onConfirm(product, finalReason);
    }
  };

  const handleClose = () => {
    setReason('');
    setCustomReason('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="text-warning d-flex align-items-center">
          <Archive size={24} className="me-2" />
          Archiver le produit
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="mb-3 d-flex">
          <Info size={18} className="me-2 flex-shrink-0 mt-1" />
          <div>
            <strong>Action réversible.</strong><br />
            Le produit disparaîtra de la caisse et des listes, mais vous
            pourrez le restaurer à tout moment depuis l'onglet Archives.
          </div>
        </Alert>

        {/* Informations du produit */}
        <div className="border rounded p-3 mb-3 bg-light">
          <div className="d-flex align-items-center mb-2">
            <Package size={20} className="me-2 text-muted" />
            <strong>{product.name}</strong>
          </div>
          <div className="small text-muted">
            {product.sku && <div>SKU : {product.sku}</div>}
            {product.categoryName && <div>Catégorie : {product.categoryName}</div>}
            <div>Stock actuel : {product.currentStock ?? product.stock ?? 0}</div>
          </div>
        </div>

        {/* Ce que ça change */}
        <div className="mb-3">
          <h6 className="small text-muted text-uppercase">Conséquences</h6>
          <ul className="small mb-0">
            <li>Retiré de la caisse et de la liste des produits</li>
            <li>Exclu des alertes de stock et des étiquettes</li>
            <li className="text-success">L'historique des ventes reste intact</li>
            <li className="text-success">Le stock est conservé en cas de restauration</li>
            <li className="text-warning">
              Son SKU et son code-barres restent réservés
            </li>
          </ul>
        </div>

        {/* Motif */}
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">
            Motif de l'archivage <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={loading}
          >
            <option value="">— Sélectionnez un motif —</option>
            {REASONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {reason === 'Autre' && (
          <Form.Group>
            <Form.Control
              type="text"
              maxLength={255}
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Précisez le motif..."
              disabled={loading}
            />
          </Form.Group>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="warning"
          onClick={handleConfirm}
          disabled={!isValid || loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Archivage...
            </>
          ) : (
            <>
              <Archive size={16} className="me-2" />
              Archiver
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ArchiveConfirmModal;